import Link from 'next/link';
import Image from 'next/legacy/image';
import {
  Box,
  Flex,
  Heading,
  Icon,
  Stack,
  Text,
  useColorMode
} from '@chakra-ui/react';
import { ArrowRightIcon } from '@heroicons/react/solid';
import { PageContainer } from '../page-container';
import { Card } from '@ui/Card';
import { SectionHeading } from './section-heading';
import {
  backendSuggestions,
  conceptsSuggestions,
  featureSuggestions,
  frontendSuggestions,
  generalSuggestions
} from './content';
import { Support } from './support';

export function DocsHome(): JSX.Element {
  const { colorMode } = useColorMode();
  const isLightMode = colorMode === 'light';

  return (
    <PageContainer>
      <Box borderBottom="1px solid" borderColor="var(--borderColor)">
        <Box
          maxW="container.lg"
          mx="auto"
          pos="relative"
          py={{ base: 12, lg: 16, xl: 24 }}
          px={{ base: 6, lg: 10 }}
        >
          <Box>
            <Heading
              as="h1"
              fontSize="32px"
              fontWeight="medium"
              mb={2}
              color="var(--primaryTextColor)"
            >
              Welcome to
              <br />
              Clerk Docs
            </Heading>

            <Text color="var(--secondaryTextColor)">
              Find all the guides and resources
              <br />
              you need to develop with Clerk.
            </Text>
          </Box>

          <Box
            pos="absolute"
            display={{ base: 'none', lg: 'block' }}
            bottom="0"
            right={0}
            sx={{
              // Chakra's `lg` breakpoint
              '@media (min-width: 992px)': {
                h: '170px',
                w: '430px'
              },

              '@media (min-width: 1080px)': {
                h: '200px',
                w: '490px'
              },

              // Chakra's `xl` breakpoint
              '@media (min-width: 1280px)': {
                h: '280px',
                w: '655px'
              }
            }}
          >
            <Image
              src={
                isLightMode
                  ? '/images/docs-hero-light.svg'
                  : '/images/docs-hero-dark.svg'
              }
              layout="fill"
              alt="Clerk components"
            />
          </Box>
        </Box>
      </Box>

      <Stack
        spacing={12}
        py={12}
        maxW="container.lg"
        mx="auto"
        px={{ base: 6, lg: 10 }}
      >
        <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2">
          {generalSuggestions.map(({ title, description, url, icon }) => (
            <Card
              key={title}
              title={title}
              description={description}
              url={url}
              IconComponent={icon}
            />
          ))}
        </div>

        <Stack spacing={3}>
          <SectionHeading thinText="Explore by" boldText="Frontend Framework" />
          <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {frontendSuggestions(isLightMode).map(
              ({ title, description, url, logoIcon, logoSize }) => (
                <Card
                  key={title}
                  title={title}
                  description={description}
                  url={url}
                  logoIcon={logoIcon}
                  logoSize={logoSize}
                />
              )
            )}
          </div>
        </Stack>

        <Stack spacing={3}>
          <SectionHeading thinText="Explore by" boldText="Backend Framework" />

          <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {backendSuggestions(isLightMode).map(
              ({ title, description, url, logoIcon, logoSize = 'sm' }) =>
                logoIcon && (
                  <Card
                    key={title}
                    title={title}
                    description={description}
                    url={url}
                    logoIcon={logoIcon}
                    logoSize={logoSize}
                  />
                )
            )}
            {backendSuggestions(isLightMode).map(
              ({ title, description, url, icon }) =>
                icon && (
                  <Card
                    key={title}
                    title={title}
                    description={description}
                    url={url}
                    IconComponent={icon}
                  />
                )
            )}
          </div>
        </Stack>

        <Stack spacing={3}>
          <SectionHeading thinText="Explore by" boldText="Feature" />

          <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2">
            {featureSuggestions.map(({ title, description, url, icon }) => (
              <Card
                key={title}
                title={title}
                description={description}
                url={url}
                IconComponent={icon}
              />
            ))}
          </div>
        </Stack>

        <Box py={12}>
          <SectionHeading boldText="Learn the concepts" size="lg" />

          {conceptsSuggestions.map(({ title, description, url }) => (
            <Flex
              key={title}
              direction={{ base: 'column', md: 'row' }}
              py={6}
              borderBottom="1px solid"
              borderColor="var(--borderColor)"
              gap={{ base: 1, md: 6 }}
            >
              <Heading
                as="h3"
                size="3xs"
                lineHeight="unset"
                flex="1"
                color="var(--primaryTextColor)"
              >
                {title}
              </Heading>

              <Stack spacing={4} flex="1">
                <Text textStyle="sm-normal" color="var(--secondaryTextColor)">
                  {description}
                </Text>

                <Link legacyBehavior href={url} passHref>
                  <Text
                    as="a"
                    color="var(--secondaryTextColor)"
                    textStyle="xs-medium"
                    role="group"
                    _hover={{
                      color: 'var(--secondaryBlue)'
                    }}
                  >
                    Learn more
                    <Icon
                      as={ArrowRightIcon}
                      ml={1}
                      pos="relative"
                      top="-1px"
                      transition="margin-left .2s"
                      _groupHover={{
                        ml: 2
                      }}
                    />
                  </Text>
                </Link>
              </Stack>
            </Flex>
          ))}
        </Box>

        <Support />
      </Stack>
    </PageContainer>
  );
}
